import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { catchError, distinctUntilChanged, map, Observable, of, skip, switchMap, take, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Page, resetFilter, TableContext } from '@markant-platform/shared/data-access';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';
import { BackofficeCartridgeService } from '@markant-platform/backoffice/data-access';
import { AuthorizerService } from '@markant-platform/authorizer/data-access';
import { UsageRightService } from '@markant-platform/usage-right/data-access';
import {
  buildFilterQueryParameter,
  Filter,
  FilterOption,
  SharedUiFilterModule,
} from '@markant-platform/shared/ui-filter';
import { Table, TableComponent } from '@markant-platform/shared/table/feature';
import {
  EMPTY_PAGE,
  HeaderAndTableCellDraft,
  TableDraft,
  TableDraftBuilder,
  TableParamConfig,
  TableRow,
} from '@markant-platform/shared/table/model';
import { deepEqual } from '@markant-platform/shared/util-helpers';
import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { CartridgeUserDTO, RoleConfig, StaticRole } from '@markant-platform/shared/user/model';
import { OrganizationDto } from '@markant-platform/shared/organization/model';
import { CartridgeDto } from '@markant-platform/shared/cartridge/model';
import {
  CARTRIDGE_MEMBER_TABLE_CELLS,
  filterOverview,
  ORGANIZATION_FILTER_KEY,
  platformAdminCellOverview,
  ROLE_FILTER_KEY,
  roleConfigOverview,
} from './configuration/cartridge-members-overview.config';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UiPageHeaderComponent } from '@markant-platform/shared/ui-components';
import {
  BookingUserRoleAssignmentsFilters,
  OrganizationUserRoleAssignmentsDto,
} from '@markant-platform/authorizer/model';
import { UserType } from 'data-model';

type AssignedCartridgeUser = {
  id: string;
  salutation?: string;
  firstname: string;
  lastname: string;
  userType: UserType;
  email: string;
  roles: string[];
};

type AssignedCartridgeUserWithLocalizedRoles = {
  rolesParams: string[];
} & AssignedCartridgeUser;

@Component({
  standalone: true,
  selector: 'markant-platform-cartridge-members-overview',
  templateUrl: './cartridge-members-overview.component.html',
  styleUrls: ['./cartridge-members-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MarkantElementsModule,
    FormsModule,
    TranslocoModule,
    SharedUiFilterModule,
    UiPageHeaderComponent,
    TableComponent,
  ],
})
export class CartridgeMembersOverviewComponent extends Table implements OnInit {
  bookingId = input<string>('');

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cartridgesService = inject(BackofficeCartridgeService);
  private readonly authorizerService = inject(AuthorizerService);
  private readonly usageRightService = inject(UsageRightService);
  private readonly userService = inject(UserService);
  private readonly authenticatedUserService = inject(AuthenticatedUserService);
  private readonly translocoService = inject(TranslocoService);
  private readonly snackbarService = inject(SnackbarService);

  private readonly ORGANIZATION_ID_FIELD = 'organizationId';
  private readonly CARTRIDGE_ID_FIELD = 'cartridgeId';

  $cartridgeId = signal(this.route.snapshot.paramMap.get(this.CARTRIDGE_ID_FIELD) ?? '');
  $organizationId = signal(this.route.snapshot.paramMap.get(this.ORGANIZATION_ID_FIELD) ?? '');
  $cartridgeVariantName = signal('');

  $sortColumnIds = computed(() => (this.$cartridgeId() !== '' ? [this.$cartridgeId()] : undefined));

  organizationIdFromBooking = this.route.snapshot.data['organizationIdFromBooking'];
  isModalOpen = false;
  roleFilterOptions: FilterOption[] = [];
  organizationFilterOptions: FilterOption[] = [];
  activeRoleConfig!: RoleConfig;
  roles: StaticRole[] = [];
  roleConfig: RoleConfig[] = roleConfigOverview;
  filter: Filter = filterOverview;
  platformAdminCell: HeaderAndTableCellDraft = platformAdminCellOverview;

  private readonly goTo = (id: string): void => {
    const user = this.getUserOrg(id);
    if (this.isPlatformAdmin()) {
      const orgId = user.organizationId ?? this.$organizationId();
      this.router.navigateByUrl(`backoffice/organizations/${orgId}/members/${user.id}`);
    } else {
      this.router.navigate([id], { relativeTo: this.route });
    }
  };

  override tableDraft: TableDraft = new TableDraftBuilder(CARTRIDGE_MEMBER_TABLE_CELLS)
    .actions([
      {
        icon: 'm_arrow-right',
        colorScheme: 'dark',
        size: '11',
        function: this.goTo as (...args: unknown[]) => unknown,
      },
    ])
    .build();

  constructor() {
    super(TableContext.CARTRIDGE_MEMBER_OVERVIEW);

    this.tableDraft = {
      ...this.tableDraft,
      sortColumnIds: this.$sortColumnIds(),
    };
  }

  // extend users object to fit table requirements
  private extendObject(users: AssignedCartridgeUser[]): AssignedCartridgeUserWithLocalizedRoles[] {
    return users.map(user => ({
      ...user,
      rolesParams: user.roles.map(item => this.getRoleDisplayName(item)).sort(),
    }));
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.$cartridgeId() && this.setCartridgeNameByLangChange(this.$cartridgeId());

    this.store?.dispatch(
      resetFilter({
        key: TableContext.CARTRIDGE_MEMBER_OVERVIEW,
        defaultFilter: {
          [this.CARTRIDGE_ID_FIELD]: [this.$cartridgeId()],
        },
      })
    );

    this.authenticatedUserService
      .getUserRoles()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(roles => roles.map(({ name }) => name as StaticRole))
      )
      .subscribe(roles => {
        this.roles = roles;
      });

    this.authenticatedUserService
      .getMatchingConfigForCurrentRouteAndRole(this.roleConfig)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: matchingRoleConfig => {
          this.activeRoleConfig = matchingRoleConfig;
          this.loadFilterOptions();

          // The organization ID is present when the user is in the context of an organization.
          // PFA doesn't need the organization name cell in that case.
          if (this.isPlatformAdmin() && !this.$organizationId()) {
            this.tableDraft.rowClickControl = true;
            this.tableDraft.headerAndTableCells.splice(
              this.tableDraft.headerAndTableCells.length - 1,
              0,
              this.platformAdminCell
            );
          }

          if (this.isServiceAdmin() && !this.roles.includes(StaticRole.PLATFORM_ADMIN)) {
            this.tableDraft.headerAndTableCells.pop();
            this.tableDraft.disabledRowLink = true;
          }

          this.loadMembers();
        },
        error: () => {
          this.snackbarService.showErrorSnackbarWithTransloco('error.common');
        },
      });
  }

  private setCartridgeNameByLangChange(cartridgeId: string): void {
    this.translocoService.langChanges$
      .pipe(
        switchMap(_ => this.fetchCartridgeVariant(cartridgeId)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(cartridge => {
        if (!cartridge) {
          return;
        }
        const { variantName } = cartridge;
        this.$cartridgeVariantName.set(variantName);
      });
  }

  private loadFilterOptions(): void {
    this.loadRoleFilterOptions();
    this.translocoService.langChanges$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.loadRoleFilterOptions());

    if (this.activeRoleConfig.additionalConfig?.['filterKeys']?.includes(ORGANIZATION_FILTER_KEY)) {
      this.usageRightService
        .getOrganizationWithUsageRightForCartridge(this.$cartridgeId(), 0, 1000)
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: data => {
            this.filter = {
              ...this.filter,
              [ORGANIZATION_FILTER_KEY]: {
                ...this.filter[ORGANIZATION_FILTER_KEY],
                filterOptions: data.result?.map((dto: OrganizationDto) => {
                  return { key: dto.id, label: dto.name };
                }),
              },
            };
            this.organizationFilterOptions = data.result?.map((dto: OrganizationDto) => {
              return { key: dto.id, label: dto.name };
            });
          },
          error: () => {
            this.snackbarService.showErrorSnackbarWithTransloco('error.common');
          },
        });
    }
  }

  private loadRoleFilterOptions(): void {
    this.authorizerService
      .getAllRolesForCartridge(
        this.$cartridgeId(),
        this.$organizationId().trim().length ? this.$organizationId() : this.organizationIdFromBooking
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.filter = {
            ...this.filter,
            [ROLE_FILTER_KEY]: {
              ...this.filter[ROLE_FILTER_KEY],
              filterOptions: data.map(role => {
                return { key: role.internalName, label: role.localizedDisplayName ?? role.internalName };
              }),
            },
          };
          this.roleFilterOptions = data.map(role => {
            return { key: role.internalName, label: role.localizedDisplayName ?? role.internalName };
          });
        },
        error: () => {
          this.snackbarService.showErrorSnackbarWithTransloco('error.common');
        },
      });
  }

  private loadMembers(): void {
    this.tableParams$
      .pipe(
        distinctUntilChanged(deepEqual),
        tap(() => this.isLoading$.next(true)),
        switchMap(tableParams => this.getCartridgeUsers(tableParams).pipe(take(1))),
        takeUntilDestroyed(this.destroyRef),
        catchError(() => {
          this.snackbarService.showErrorSnackbarWithTransloco('cartridge.members.loadingError');
          return of(EMPTY_PAGE);
        })
      )
      .subscribe(({ result, totalCount }: Page<AssignedCartridgeUser>) => {
        this.totalCount = totalCount;
        this.reload(this.extendObject(result));
      });
  }

  getRoleDisplayName(internalName: string): string {
    const localizedDisplayName = this.roleFilterOptions.find(filterOption => filterOption.key === internalName)?.label;
    return localizedDisplayName ?? internalName;
  }

  isOrgAdmin(): boolean {
    return this.activeRoleConfig?.role === StaticRole.ORGANIZATION_ADMIN;
  }

  isServiceAdmin(): boolean {
    return this.activeRoleConfig?.role === StaticRole.LEISTUNG_ADMIN;
  }

  isPlatformAdmin(): boolean {
    return this.activeRoleConfig?.role === StaticRole.PLATFORM_ADMIN;
  }

  private getCartridgeUsers(tableParams: TableParamConfig): Observable<Page<AssignedCartridgeUser>> {
    return this.authenticatedUserService.getOrganizationId().pipe(
      switchMap(activeOrganizationId => {
        if (this.activeRoleConfig?.role === StaticRole.ORGANIZATION_ADMIN) {
          this.$organizationId.set(activeOrganizationId);
        }
        // fetch assigned users when requester in an organization context
        if (this.$organizationId()?.trim().length) {
          const filters: BookingUserRoleAssignmentsFilters = {
            role: tableParams.activeFilter[ROLE_FILTER_KEY] || [],
            query: tableParams.searchQuery,
            index: tableParams.page.index - 1,
            size: tableParams.page.size,
          };
          return this.authorizerService.getUserRoleAssignmentsForBooking(this.bookingId(), filters).pipe(
            tap(({ isCartridgeVariantActive }) => {
              if (!isCartridgeVariantActive) {
                this.tableDraft = new TableDraftBuilder(CARTRIDGE_MEMBER_TABLE_CELLS).disableRowLink().build();
              }
            }),
            map(this.mapAuthorizerServiceDtoToAssignedCartridgeUser)
          );
        } else {
          // fetch assigned users when requester is not in an organization context
          return this.userService.getCartridgeUsers(
            this.$cartridgeId(),
            tableParams.page.index - 1,
            tableParams.page.size,
            tableParams.searchQuery,
            buildFilterQueryParameter(
              [...(this.activeRoleConfig.additionalConfig?.['filterKeys'] ?? []), 'organization-id'],
              {
                ...tableParams.activeFilter,
                ...(!!this.organizationIdFromBooking && { 'organization-id': [this.organizationIdFromBooking] }),
              }
            )
          );
        }
      })
    );
  }

  mapAuthorizerServiceDtoToAssignedCartridgeUser(
    response: OrganizationUserRoleAssignmentsDto
  ): Page<AssignedCartridgeUser> {
    return {
      ...response.userRoleAssignments,
      result: response.userRoleAssignments.result.map(value => {
        return {
          id: value.userId,
          firstname: value.firstName,
          lastname: value.lastName,
          email: value.email,
          userType: value.userType,
          salutation: value.salutation,
          roles: value.roleNames,
        };
      }),
    };
  }

  onRowClick({ entryId, object }: TableRow): void {
    const { organizationId } = object as CartridgeUserDTO;
    this.router.navigate(['backoffice', 'organizations', organizationId, 'members', entryId]);
  }

  private fetchCartridgeVariant(cartridgeId: string): Observable<CartridgeDto | undefined> {
    return this.cartridgesService.getCartridgeVariantById(cartridgeId);
  }

  private getUserOrg(userId: string): CartridgeUserDTO {
    return this.tableData$.value.tableRows.find(({ entryId }) => entryId === userId)?.object as CartridgeUserDTO;
  }
}
